import axios from "axios";
import { getAuthHeader } from "./authService";

const API_URL = `${process.env.REACT_APP_SERVER_URL}/api/reports`;

export type ReportPeriod = "1m" | "3m" | "6m" | "1y" | "ytd" | "all";

export type ReportQuery = {
  period?: ReportPeriod;
  status?: string;
  sales_rep_id?: string | number;
  publication_id?: string | number;
  issue_id?: string | number;
};

export type PipelineSummary = Record<string, { count: number; value: number }>;

export type PipelineRow = {
  id: number;
  status: string;
  company: string;
  sales_rep: string;
  publications: string;
  line_count: number;
  total: number;
  updated_at: string;
};

export type PipelineReport = {
  period: ReportPeriod;
  isPersonalView: boolean;
  summary: PipelineSummary;
  rows: PipelineRow[];
};

export type RepPerformanceRow = {
  sales_rep_id: number;
  sales_rep: string;
  draft_count: number;
  sent_count: number;
  signed_count: number;
  draft_value: number;
  sent_value: number;
  signed_value: number;
};

export type RepPerformanceReport = {
  period: ReportPeriod;
  isPersonalView: boolean;
  reps: RepPerformanceRow[];
};

export type RevenueReport = {
  period: ReportPeriod;
  isPersonalView: boolean;
  signed: { count: number; revenue: number };
  invoices: {
    count: number;
    invoiced_revenue: number;
    paid_count: number;
    paid_revenue: number;
    by_status: Record<string, { count: number; value: number }>;
  };
  recent_paid: Array<{
    id: number;
    invoice_number: string;
    company: string;
    sales_rep: string;
    amount: number;
    paid_at: string | null;
  }>;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getPipelineReport = async (params: ReportQuery = {}): Promise<PipelineReport | null> => {
  try {
    const response = await axios.get(`${API_URL}/pipeline`, {
      headers: getAuthHeader(),
      params,
    });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return null;
  }
};

export const getRepPerformanceReport = async (
  params: ReportQuery = {}
): Promise<RepPerformanceReport | null> => {
  try {
    const response = await axios.get(`${API_URL}/rep-performance`, {
      headers: getAuthHeader(),
      params,
    });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return null;
  }
};

export const getRevenueReport = async (params: ReportQuery = {}): Promise<RevenueReport | null> => {
  try {
    const response = await axios.get(`${API_URL}/revenue`, {
      headers: getAuthHeader(),
      params,
    });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return null;
  }
};

export const downloadPipelineCsv = async (params: ReportQuery = {}) => {
  const response = await axios.get(`${API_URL}/pipeline/export`, {
    headers: getAuthHeader(),
    params,
    responseType: "blob",
  });
  downloadBlob(response.data, "pipeline-report.csv");
};

export const downloadRepPerformanceCsv = async (params: ReportQuery = {}) => {
  const response = await axios.get(`${API_URL}/rep-performance/export`, {
    headers: getAuthHeader(),
    params,
    responseType: "blob",
  });
  downloadBlob(response.data, "rep-performance.csv");
};
